export type AIPlan = 'FREE' | 'FACHOWIEC' | 'PRO' | 'ADMIN';
export type AIFeature = 'textAssistant' | 'vision';
export type AIQuotaWindow = 'daily' | 'monthly';
export type AIQuotaLimit = number | 'unlimited';
export type AIQuotaFailureKind = 'app_plan_limit' | 'provider_limit' | 'backend_error';

export interface AIQuotaStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface PlanQuota {
  window: AIQuotaWindow;
  limit: AIQuotaLimit;
}

export interface AIPlanLimits {
  textAssistant: PlanQuota;
  vision: PlanQuota;
  maxProjects: number | 'unlimited';
  pdfBranding: boolean;
}

export interface AIQuotaStatus {
  plan: AIPlan;
  feature: AIFeature;
  key: string;
  window: AIQuotaWindow;
  used: number;
  limit: AIQuotaLimit;
  remaining: number | 'unlimited';
  allowed: boolean;
  isUnlimited: boolean;
}

export const AI_PLAN_LIMITS: Record<AIPlan, AIPlanLimits> = {
  FREE: {
    textAssistant: { window: 'daily', limit: 5 },
    vision: { window: 'daily', limit: 2 },
    maxProjects: 3,
    pdfBranding: true,
  },
  FACHOWIEC: {
    textAssistant: { window: 'monthly', limit: 300 },
    vision: { window: 'monthly', limit: 60 },
    maxProjects: 'unlimited',
    pdfBranding: false,
  },
  PRO: {
    textAssistant: { window: 'monthly', limit: 1500 },
    vision: { window: 'monthly', limit: 300 },
    maxProjects: 'unlimited',
    pdfBranding: false,
  },
  ADMIN: {
    textAssistant: { window: 'daily', limit: 'unlimited' },
    vision: { window: 'daily', limit: 'unlimited' },
    maxProjects: 'unlimited',
    pdfBranding: false,
  },
};

const FEATURE_STORAGE_LABEL: Record<AIFeature, 'text' | 'vision'> = {
  textAssistant: 'text',
  vision: 'vision',
};

export function getDemoAIPlan(isDev: boolean): AIPlan {
  // MVP placeholder only. Production quota must be enforced server-side from the real user plan.
  return isDev ? 'ADMIN' : 'FREE';
}

export function resolveAIPlanFromRole(role?: string | null, isDev = false): AIPlan {
  if (isDev) return 'ADMIN';

  const normalized = (role ?? '').trim().toLowerCase();
  if (normalized === 'admin' || normalized === 'owner') return 'ADMIN';
  if (normalized === 'pro') return 'PRO';
  if (normalized === 'fachowiec') return 'FACHOWIEC';
  return 'FREE';
}

export function getAIQuotaStatus(
  feature: AIFeature,
  plan: AIPlan,
  storage: AIQuotaStorage | null = getDefaultQuotaStorage(),
  now = new Date()
): AIQuotaStatus {
  const quota = AI_PLAN_LIMITS[plan][feature];
  const key = buildAIQuotaKey(feature, quota.window, now);
  const used = readUsageCount(storage, key);
  const isUnlimited = quota.limit === 'unlimited';
  const remaining =
    quota.limit === 'unlimited' ? 'unlimited' : Math.max(quota.limit - used, 0);

  return {
    plan,
    feature,
    key,
    window: quota.window,
    used,
    limit: quota.limit,
    remaining,
    allowed: quota.limit === 'unlimited' || used < quota.limit,
    isUnlimited,
  };
}

export function consumeAIQuota(
  feature: AIFeature,
  plan: AIPlan,
  storage: AIQuotaStorage | null = getDefaultQuotaStorage(),
  now = new Date()
): AIQuotaStatus {
  const current = getAIQuotaStatus(feature, plan, storage, now);
  if (!current.allowed || current.isUnlimited || !storage) return current;

  storage.setItem(current.key, String(current.used + 1));
  return getAIQuotaStatus(feature, plan, storage, now);
}

export function buildAIQuotaKey(feature: AIFeature, window: AIQuotaWindow, now = new Date()): string {
  const datePart =
    window === 'daily'
      ? now.toISOString().slice(0, 10)
      : now.toISOString().slice(0, 7);

  return `prowycena_ai_${FEATURE_STORAGE_LABEL[feature]}_usage_${datePart}`;
}

export function classifyAIQuotaFailure(reason?: string): AIQuotaFailureKind {
  if (reason === 'app_daily_limit_reached' || reason === 'app_plan_limit') {
    return 'app_plan_limit';
  }

  if (
    reason === 'provider_quota_exhausted' ||
    reason === 'quota_exhausted' ||
    reason === 'rate_limited' ||
    reason === '429'
  ) {
    return 'provider_limit';
  }

  return 'backend_error';
}

export function isAIQuotaUnlimited(status: AIQuotaStatus): boolean {
  return status.isUnlimited;
}

function getDefaultQuotaStorage(): AIQuotaStorage | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
}

function readUsageCount(storage: AIQuotaStorage | null, key: string): number {
  if (!storage) return 0;

  const parsed = Number.parseInt(storage.getItem(key) ?? '0', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}
